import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Logo from '../components/Logo';

// Public home page: what Smart Maji offers, why, and how to get in touch.
// Big buttons send customers to buy a token (/pay) and staff to sign in (/login).
// Fonts (loaded in public/index.html): Bricolage Grotesque for headings, IBM Plex Sans for text, IBM Plex Mono for numbers.

const C = {
  paper: '#f5f2ec', paper2: '#ece7dd', ink: '#0d1b2a', body: '#3d4a57', muted: '#6b7680', rule: '#d9d2c5',
  water: '#0a7ea4', waterDark: '#075f7c', night: '#0d1b2a', mist: '#dcecf1',
};
const DISPLAY = "'Bricolage Grotesque', 'IBM Plex Sans', system-ui, sans-serif";
const TEXT = "'IBM Plex Sans', system-ui, -apple-system, 'Segoe UI', sans-serif";
const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const wrap = { maxWidth: 1140, margin: '0 auto', padding: '0 24px', boxSizing: 'border-box' };

const WHY = [
  ['Your money, your account.', 'Customers pay into your own paybill, till or bank account, not ours.'],
  ['Tokens in seconds.', 'Sent by SMS and WhatsApp as soon as the payment clears, day or night.'],
  ['Paid before the water flows.', 'Prepaid means no meter readings to argue over and no arrears to chase.'],
  ['Your name on the door.', 'Customers buy on your own site, yourname.smartwater.co.ke.'],
  ['Every shilling accounted for.', 'Live dashboard, monthly statements and a full record of who did what.'],
  ['Nobody pays twice.', 'If a token is held up, we trace the payment and deliver it.'],
  ['Access for the right people.', 'Caretakers and managers see only the sites and accounts they run.'],
  ['More ways to pay.', 'M-Pesa today, with Airtel Money and bank paybills such as KCB on the way.'],
];

const SERVICES = [
  ['Prepaid meter management', 'All your STS water meters in one place, with tamper codes and manual tokens when you need them.'],
  ['Mobile money collection', 'An M-Pesa prompt on the customer’s phone, or a payment straight to your paybill. The token is calculated and sent automatically.'],
  ['Your own payment site', 'A branded page where your customers buy water at any hour, without calling anyone.'],
  ['Reporting', 'Collections, water dispensed and top customers by month, account and site, plus a statement emailed on the 1st.'],
  ['Follow-up on every payment', 'Stuck payments are flagged and fixed, and customers are told what happened.'],
];

const STEPS = [
  ['Pay', 'Enter the meter number and amount, then approve the prompt on your phone. You can also pay the paybill directly.'],
  ['Receive', 'Your 20-digit token arrives by SMS and WhatsApp within seconds, and appears on screen.'],
  ['Load', 'Type the token into the meter. Your water units load straight away.'],
];

const AUDIENCE = ['Landlords and property managers', 'Estates and gated communities', 'Water service providers', 'Schools, hospitals and institutions', 'Water kiosks'];

// True on phone-width screens; used to switch the animations to a taller layout.
function useNarrow(query = '(max-width: 600px)') {
  const [match, setMatch] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const update = () => setMatch(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, [query]);
  return match;
}

// ───────────── Animation slot ─────────────
// Plays the clip set under Messaging → Settings → Public home page (video, image/GIF,
// or a YouTube/Vimeo link). Until one is set, the built-in animation below plays.
function MediaSlot({ url }) {
  if (url) {
    const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
    const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    const frame = { position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 };
    let media;
    if (yt) {
      media = <iframe title="How it works" src={`https://www.youtube-nocookie.com/embed/${yt[1]}?autoplay=1&mute=1&loop=1&playlist=${yt[1]}&controls=0&rel=0`} allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen style={frame} />;
    } else if (vimeo) {
      media = <iframe title="How it works" src={`https://player.vimeo.com/video/${vimeo[1]}?autoplay=1&muted=1&loop=1&background=1`} allow="autoplay; fullscreen; picture-in-picture" style={frame} />;
    } else if (/\.(mp4|webm|mov)(\?|$)/i.test(url)) {
      media = <video src={url} autoPlay muted loop playsInline style={{ ...frame, objectFit: 'cover' }} aria-label="How it works" />;
    } else {
      media = <img src={url} alt="How Smart Maji works" style={{ ...frame, objectFit: 'cover' }} />;
    }
    return <div style={{ position: 'relative', aspectRatio: '16 / 11', borderRadius: 6, overflow: 'hidden', background: C.night }}>{media}</div>;
  }
  return <FlowAnimation />;
}

const PROVIDERS = [
  { name: 'M-PESA', color: '#43b02a', label: 'Lipa na M-Pesa', amount: '500', ref: 'Paybill 123 456' },
  { name: 'Airtel Money', color: '#e40000', label: 'Airtel Money', amount: '300', ref: 'Till 123 456' },
  { name: 'KCB', color: '#2e7d32', label: 'Bank paybill', amount: '1,000', ref: 'Paybill 522 522' },
];
const TOKEN_GROUPS = ['4821', '0937', '1166', '2205', '7713'];

// One 12-second story, repeated three times with a different payment option each time (36 s in all):
//   pay on the phone → token arrives by SMS → token typed into the CIU → meter valve opens → water flows.
const LOOP = 12;
const typeKeyframes = TOKEN_GROUPS.map((_, i) => {
  const at = 44 + i * 4;
  return `@keyframes mwType${i} { 0%,${at}% { opacity: 0 } ${at + 1}%,62% { opacity: 1 } 64%,100% { opacity: 0 } }
          @keyframes mwKey${i} { 0%,${at - 1}% { fill: #24384f } ${at}% { fill: #5ec4e6 } ${at + 2}%,100% { fill: #24384f } }`;
}).join('\n');

function FlowAnimation() {
  const keys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '✕', '0', '↵'];
  const flashKeys = { 4: 0, 8: 1, 1: 2, 6: 3, 11: 4 }; // key index → token group it "types"
  // On phones the scene is re-stacked into a taller frame: phone and keypad on top,
  // meter and tap underneath with the four steps beside them, so nothing shrinks to unreadable.
  const narrow = useNarrow();
  return (
    <figure style={{ margin: 0 }}>
      <div role="img" aria-label="A customer pays by M-Pesa, Airtel Money or a bank paybill, receives a token by SMS, types it into the customer interface unit, and the meter valve opens so water flows"
        style={{ position: 'relative', aspectRatio: narrow ? '400 / 560' : '16 / 11', borderRadius: 6, overflow: 'hidden', background: C.night }}>
        <style>{`
          .mw-anim .cyc { opacity: 0; animation: mwCycle ${LOOP * 3}s infinite }
          .mw-anim .cyc-2 { animation-delay: ${LOOP}s } .mw-anim .cyc-3 { animation-delay: ${LOOP * 2}s }
          @keyframes mwCycle { 0% { opacity: 0 } 0.6%, 32.6% { opacity: 1 } 33.3%, 100% { opacity: 0 } }
          .mw-anim .chip { animation: mwChip ${LOOP * 3}s infinite; fill-opacity: .25 }
          .mw-anim .chip-2 { animation-delay: ${LOOP}s } .mw-anim .chip-3 { animation-delay: ${LOOP * 2}s }
          @keyframes mwChip { 0% { fill-opacity: .25 } 0.6%, 32.6% { fill-opacity: 1 } 33.3%, 100% { fill-opacity: .25 } }
          .mw-anim .loop { animation-duration: ${LOOP}s; animation-iteration-count: infinite; animation-timing-function: linear }
          @keyframes mwTick { 0%,12% { opacity: 0 } 15%,96% { opacity: 1 } 100% { opacity: 0 } }
          @keyframes mwSms { 0%,25% { opacity: 0; transform: translateY(14px) } 29%,96% { opacity: 1; transform: none } 100% { opacity: 0 } }
          @keyframes mwAccept { 0%,63% { opacity: 0 } 65%,96% { opacity: 1 } 100% { opacity: 0 } }
          @keyframes mwSignal { 0%,64% { stroke-dashoffset: 60; opacity: 0 } 66% { opacity: 1 } 72% { stroke-dashoffset: 0; opacity: 1 } 76%,100% { stroke-dashoffset: 0; opacity: 0 } }
          @keyframes mwClosed { 0%,71% { opacity: 1 } 73%,97% { opacity: 0 } 100% { opacity: 1 } }
          @keyframes mwOpen { 0%,71% { opacity: 0 } 73%,97% { opacity: 1 } 100% { opacity: 0 } }
          @keyframes mwStream { 0%,74% { transform: scaleY(0) } 78%,95% { transform: scaleY(1) } 98%,100% { transform: scaleY(0) } }
          @keyframes mwPool { 0%,78% { transform: scaleX(0); opacity: 0 } 90%,96% { transform: scaleX(1); opacity: 1 } 100% { transform: scaleX(1); opacity: 0 } }
          @keyframes mwDrip { 0% { transform: translateY(0); opacity: 1 } 100% { transform: translateY(44px); opacity: 0 } }
          @keyframes mwWaterOn { 0%,75% { opacity: 0 } 78%,96% { opacity: 1 } 100% { opacity: 0 } }
          @keyframes mwCap1 { 0%,24% { fill: #f5f2ec } 26%,100% { fill: #5d7186 } }
          @keyframes mwCap2 { 0%,24% { fill: #5d7186 } 26%,41% { fill: #f5f2ec } 43%,100% { fill: #5d7186 } }
          @keyframes mwCap3 { 0%,41% { fill: #5d7186 } 43%,70% { fill: #f5f2ec } 72%,100% { fill: #5d7186 } }
          @keyframes mwCap4 { 0%,70% { fill: #5d7186 } 72%,98% { fill: #f5f2ec } 100% { fill: #5d7186 } }
          ${typeKeyframes}
          .mw-anim .drip { animation: mwDrip .7s linear infinite }
          @media (prefers-reduced-motion: reduce) {
            .mw-anim .cyc, .mw-anim .chip, .mw-anim .loop, .mw-anim .drip { animation: none !important }
            .mw-anim .cyc-1, .mw-anim .sms, .mw-anim .tick, .mw-anim .accept, .mw-anim .open, .mw-anim .wateron, .mw-anim .pool { opacity: 1 }
            .mw-anim .chip { fill-opacity: 1 } .mw-anim .closed, .mw-anim .typed, .mw-anim .signal { opacity: 0 }
            .mw-anim .stream { transform: none } .mw-anim .cap { fill: #f5f2ec }
          }
        `}</style>
        <svg className="mw-anim" viewBox={narrow ? '0 0 400 560' : '0 0 640 440'} width="100%" height="100%" style={{ position: 'absolute', inset: 0 }}>
          <defs>
            <pattern id="mwGrid" width="32" height="32" patternUnits="userSpaceOnUse">
              <path d="M32 0H0V32" fill="none" stroke="#ffffff" strokeOpacity=".04" />
            </pattern>
          </defs>
          <rect width="640" height="560" fill="url(#mwGrid)" />

          {/* Payment options — the current one lights up */}
          <g fontFamily={TEXT} fontSize="12" fontWeight="600" transform={narrow ? 'translate(4 0)' : undefined}>
            {PROVIDERS.map((p, i) => {
              const x = 40 + i * 104;
              return (
                <g key={p.name}>
                  <rect className={`chip chip-${i + 1}`} x={x} y="22" width="96" height="24" rx="3" fill={p.color} />
                  <text x={x + 48} y="38" textAnchor="middle" fill="#fff">{p.name === 'KCB' ? 'KCB paybill' : p.name}</text>
                </g>
              );
            })}
          </g>

          <g transform={narrow ? 'translate(-7 0)' : undefined}>
          {/* ── Phone ── */}
          <rect x="40" y="66" width="150" height="272" rx="22" fill="#16263a" stroke="#2b415b" strokeWidth="2" />
          <rect x="52" y="90" width="126" height="226" rx="10" fill="#f5f2ec" />
          <rect x="99" y="75" width="32" height="5" rx="2.5" fill="#2b415b" />
          {PROVIDERS.map((p, i) => (
            <g key={p.name} className={`cyc cyc-${i + 1}`}>
              <path d="M52 100 a10 10 0 0 1 10 -10 h106 a10 10 0 0 1 10 10 v28 h-126 z" fill={p.color} />
              <text x="115" y="115" textAnchor="middle" fontSize={p.name.length > 6 ? 14 : 16} fontWeight="700" fill="#fff" fontFamily={DISPLAY}>{p.name}</text>
              <text x="115" y="146" textAnchor="middle" fontSize="10" fill="#6b7680" fontFamily={MONO}>{p.ref}</text>
              <text x="115" y="176" textAnchor="middle" fontSize="26" fontWeight="700" fill="#0d1b2a" fontFamily={DISPLAY}>{p.amount}</text>
              <text x="115" y="190" textAnchor="middle" fontSize="10" fill="#6b7680" fontFamily={TEXT}>KES</text>
            </g>
          ))}
          <g className="tick loop" style={{ animationName: 'mwTick' }}>
            <rect x="80" y="198" width="70" height="20" rx="3" fill="#0d1b2a" />
            <text x="115" y="212" textAnchor="middle" fontSize="11" fontWeight="600" fill="#fff" fontFamily={TEXT}>✓ Paid</text>
          </g>
          {/* SMS with the token */}
          <g className="sms loop" style={{ animationName: 'mwSms' }}>
            <rect x="60" y="228" width="110" height="78" rx="8" fill="#dcecf1" />
            <text x="68" y="244" fontSize="9" fontWeight="600" fill="#0a7ea4" fontFamily={TEXT}>SMS · Smart Maji</text>
            <text x="68" y="258" fontSize="9" fill="#3d4a57" fontFamily={TEXT}>Your token:</text>
            <text x="68" y="272" fontSize="10" fill="#0d1b2a" fontFamily={MONO}>{TOKEN_GROUPS.slice(0, 3).join(' ')}</text>
            <text x="68" y="285" fontSize="10" fill="#0d1b2a" fontFamily={MONO}>{TOKEN_GROUPS.slice(3).join(' ')}</text>
            <text x="68" y="299" fontSize="9" fill="#3d4a57" fontFamily={TEXT}>Units: 2.0 m³</text>
          </g>

          {/* ── CIU (customer interface unit) keypad ── */}
          <rect x="236" y="96" width="138" height="232" rx="12" fill="#1b2d43" stroke="#2b415b" strokeWidth="2" />
          <rect x="248" y="110" width="114" height="46" rx="4" fill="#0a1420" />
          <g fontFamily={MONO} fontSize="11" fill="#5ec4e6">
            {TOKEN_GROUPS.map((g, i) => (
              <text key={g} className="typed loop" x={254 + (i % 3) * 36} y={i < 3 ? 128 : 146} style={{ animationName: `mwType${i}` }}>{g}</text>
            ))}
          </g>
          <g className="accept loop" style={{ animationName: 'mwAccept' }}>
            <text x="305" y="130" textAnchor="middle" fontSize="12" fontWeight="600" fill="#4ade80" fontFamily={TEXT}>✓ ACCEPTED</text>
            <text x="305" y="146" textAnchor="middle" fontSize="11" fill="#9fb0c0" fontFamily={MONO}>+2.0 m³</text>
          </g>
          {keys.map((k, i) => {
            const x = 252 + (i % 3) * 36;
            const y = 170 + Math.floor(i / 3) * 36;
            const g = flashKeys[i];
            return (
              <g key={k}>
                <rect className={g !== undefined ? 'loop' : undefined} x={x} y={y} width="30" height="28" rx="4" fill="#24384f"
                  style={g !== undefined ? { animationName: `mwKey${g}` } : undefined} />
                <text x={x + 15} y={y + 19} textAnchor="middle" fontSize="13" fill="#c6d2dd" fontFamily={TEXT}>{k}</text>
              </g>
            );
          })}
          <text x="305" y="346" textAnchor="middle" fontSize={narrow ? 12 : 10} fill="#9fb0c0" fontFamily={TEXT}>Customer interface unit</text>
          </g>

          {/* Signal from the CIU to the meter */}
          <path className="signal loop" d={narrow ? 'M262 360 Q 200 384 148 372' : 'M376 150 Q 410 120 440 140'} fill="none" stroke="#5ec4e6" strokeWidth="2.5" strokeDasharray="6 5"
            style={{ animationName: 'mwSignal', strokeDashoffset: 60 }} />

          <g transform={narrow ? 'translate(-410 220)' : undefined}>
          {/* ── Meter with valve, pipe and tap ── */}
          <path d="M430 200 H 600" stroke="#24384f" strokeWidth="14" />
          <circle cx="500" cy="180" r="52" fill="#e9e4da" stroke="#b9b1a3" strokeWidth="4" />
          <circle cx="500" cy="180" r="40" fill="#f5f2ec" />
          <text x="500" y="166" textAnchor="middle" fontSize="9" fontWeight="600" fill="#0a7ea4" fontFamily={TEXT} letterSpacing="1.5">METER</text>
          <g className="closed loop" style={{ animationName: 'mwClosed' }}>
            <circle cx="500" cy="186" r="9" fill="#c0392b" />
            <text x="500" y="210" textAnchor="middle" fontSize="8" fill="#c0392b" fontFamily={TEXT} fontWeight="600">VALVE SHUT</text>
          </g>
          <g className="open loop" style={{ animationName: 'mwOpen' }}>
            <circle cx="500" cy="186" r="9" fill="#2e9e4f" />
            <text x="500" y="210" textAnchor="middle" fontSize="8" fill="#2e9e4f" fontFamily={TEXT} fontWeight="600">VALVE OPEN</text>
          </g>
          {/* Tap */}
          <path d="M586 193 h18 a10 10 0 0 1 10 10 v26" fill="none" stroke="#9aa7b4" strokeWidth="10" strokeLinecap="round" />
          <rect x="604" y="226" width="20" height="8" rx="2" fill="#9aa7b4" />
          {/* Water */}
          <rect className="stream loop" x="608" y="236" width="12" height="84" fill="#5ec4e6"
            style={{ animationName: 'mwStream', transformOrigin: '614px 236px', transformBox: 'view-box' }} />
          <g className="wateron loop" style={{ animationName: 'mwWaterOn' }}>
            {[0, 0.23, 0.46].map((d) => (
              <circle key={d} className="drip" cx={600 + d * 40} cy="300" r="3" fill="#7dd3fc" style={{ animationDelay: `${d}s` }} />
            ))}
            <text x="560" y="258" textAnchor="end" fontSize="12" fontWeight="600" fill="#5ec4e6" fontFamily={TEXT}>Water on</text>
          </g>
          <ellipse className="pool loop" cx="614" cy="324" rx="24" ry="5" fill="#5ec4e6" fillOpacity=".5"
            style={{ animationName: 'mwPool', transformOrigin: '614px 324px', transformBox: 'view-box' }} />
          </g>

          {/* Steps */}
          <g fontFamily={TEXT} fontSize={narrow ? 16 : 13} fontWeight="600">
            {['1  Pay', '2  Get token', '3  Enter on CIU', '4  Water flows'].map((t, i) => (
              <text key={t} className="cap loop" x={narrow ? 250 : 40 + i * 150} y={narrow ? 404 + i * 32 : 398} fill="#5d7186" style={{ animationName: `mwCap${i + 1}` }}>{t}</text>
            ))}
          </g>
        </svg>
      </div>
      <figcaption style={{ fontFamily: TEXT, fontSize: 12, color: '#9fb0c0', marginTop: 10 }}>
        M-Pesa available now. Airtel Money and bank paybills coming soon.
      </figcaption>
    </figure>
  );
}

// ───────────── Enquiry form ─────────────
function EnquiryForm() {
  const [f, setF] = useState({ name: '', organisation: '', phone: '', email: '', meters: '', message: '', website: '' });
  const [state, setState] = useState({ busy: false, done: false, error: '' });
  const field = { width: '100%', boxSizing: 'border-box', border: `1px solid ${C.rule}`, borderRadius: 4, padding: '12px 14px', fontSize: 15, fontFamily: TEXT, color: C.ink, background: '#fffdf9' };
  const lbl = { display: 'block', fontSize: 13, fontWeight: 600, color: C.ink, marginBottom: 6, fontFamily: TEXT };
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setState({ busy: true, done: false, error: '' });
    try {
      await axios.post('/api/public/enquiries', f);
      setState({ busy: false, done: true, error: '' });
    } catch (err) {
      setState({ busy: false, done: false, error: err.response?.data?.message || 'Sorry, that didn’t send. Please call or email us instead.' });
    }
  };

  if (state.done) {
    return (
      <div role="status" style={{ borderTop: `3px solid ${C.water}`, background: '#fffdf9', padding: 28 }}>
        <h3 style={{ fontFamily: DISPLAY, color: C.ink, fontSize: 24, margin: '0 0 8px' }}>Thanks, {f.name.split(' ')[0]}.</h3>
        <p style={{ color: C.body, margin: 0, fontFamily: TEXT }}>We have your message and will get back to you shortly.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate style={{ background: '#fffdf9', borderTop: `3px solid ${C.ink}`, padding: 26 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 14 }}>
        <div><label style={lbl} htmlFor="enq-name">Name *</label><input id="enq-name" required value={f.name} onChange={set('name')} autoComplete="name" style={field} /></div>
        <div><label style={lbl} htmlFor="enq-org">Organisation or property</label><input id="enq-org" value={f.organisation} onChange={set('organisation')} autoComplete="organization" style={field} /></div>
        <div><label style={lbl} htmlFor="enq-phone">Phone</label><input id="enq-phone" type="tel" value={f.phone} onChange={set('phone')} autoComplete="tel" placeholder="07…" style={field} /></div>
        <div><label style={lbl} htmlFor="enq-email">Email</label><input id="enq-email" type="email" value={f.email} onChange={set('email')} autoComplete="email" style={field} /></div>
        <div>
          <label style={lbl} htmlFor="enq-meters">Number of meters</label>
          <select id="enq-meters" value={f.meters} onChange={set('meters')} style={field}>
            <option value="">Choose…</option>
            <option>1–20</option><option>21–100</option><option>101–500</option><option>500+</option><option>Not sure yet</option>
          </select>
        </div>
      </div>
      <div style={{ marginTop: 14 }}>
        <label style={lbl} htmlFor="enq-msg">What would you like to do?</label>
        <textarea id="enq-msg" rows={4} value={f.message} onChange={set('message')} style={{ ...field, resize: 'vertical' }}
          placeholder="e.g. 40 units in Kasarani. We want tenants to pay into our own paybill." />
      </div>
      {/* Honeypot: hidden from people, bots fill it in */}
      <div aria-hidden="true" style={{ position: 'absolute', left: -9999, width: 1, height: 1, overflow: 'hidden' }}>
        <label htmlFor="enq-web">Website</label><input id="enq-web" tabIndex={-1} autoComplete="off" value={f.website} onChange={set('website')} />
      </div>
      {state.error && <div role="alert" style={{ color: '#9b1c1c', background: '#fdf0ee', borderLeft: '3px solid #c0392b', padding: '10px 14px', marginTop: 14, fontSize: 14, fontFamily: TEXT }}>{state.error}</div>}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 20, flexWrap: 'wrap' }}>
        <button type="submit" disabled={state.busy}
          style={{ background: C.ink, color: '#fff', border: 'none', borderRadius: 4, padding: '14px 26px', fontSize: 16, fontWeight: 600, fontFamily: TEXT, cursor: 'pointer', opacity: state.busy ? 0.7 : 1 }}>
          {state.busy ? 'Sending…' : 'Send enquiry'}
        </button>
        <span style={{ color: C.muted, fontSize: 13, fontFamily: TEXT }}>Leave a phone number or email so we can reach you.</span>
      </div>
    </form>
  );
}

const SectionLabel = ({ n, children, light }) => (
  <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, fontFamily: MONO, fontSize: 12, letterSpacing: 1, textTransform: 'uppercase', color: light ? '#9fb0c0' : C.water, marginBottom: 14 }}>
    <span>{n}</span><span style={{ height: 1, width: 40, background: 'currentColor', opacity: 0.5, alignSelf: 'center' }} /><span>{children}</span>
  </div>
);

// ───────────── Meter types showcase ─────────────
// A dark stage on the left animates one family of meters at a time; the list on the right
// names the meters in that family. It moves on by itself every 9 s (paused while the pointer
// is over it), and stops moving on once someone picks a family. With reduced motion turned on,
// each scene shows a still picture and nothing advances on its own.
const METERS = [
  {
    key: 'prepaid', title: 'Prepaid meters', lead: 'Water flows only while there is credit.',
    alt: 'Three prepaid meters: a keypad unit where a token is typed in, a smart card slid into a meter, and a phone sending credit straight to an internet-connected meter',
    types: [
      ['Split keypad (STS)', 'Meter and valve at the pipe, keypad indoors. The customer types the 20-digit token.'],
      ['Integrated keypad', 'Keypad, meter and valve in one body, typed in at the meter.'],
      ['Smart card', 'Credit loaded onto a card at a vending point, then inserted at the meter.'],
      ['IoT, tokenless', 'Credit sent straight to the meter over GSM, NB-IoT or LoRaWAN. Nothing to type.'],
      ['Water ATM', 'Pay-per-litre kiosks that dispense with a card or tag.'],
    ],
  },
  {
    key: 'mechanical', title: 'Mechanical meters', lead: 'The everyday meter, read by hand and billed afterwards.',
    alt: 'A mechanical water meter on a pipe, its dials turning and register counting up as water passes',
    types: [
      ['Single-jet', 'Small homes (DN15 to DN25). Low cost, and a common base for prepaid add-ons.'],
      ['Multi-jet', 'Homes and small businesses. More accurate when flow is low.'],
      ['Volumetric', 'The most accurate at low flow, so it catches small leaks.'],
      ['Dry-dial', 'Register sealed away from the water so it stays readable.'],
    ],
  },
  {
    key: 'bulk', title: 'Bulk and distribution meters', lead: 'For mains, boreholes and the supply into an estate.',
    alt: 'A large water main with three bulk meters: a turbine meter with a spinning rotor, an electromagnetic meter with pulsing coils, and a clamp-on ultrasonic meter sending sound pulses across the pipe',
    types: [
      ['Woltmann turbine', 'DN50 to DN500 for mains, borehole output and zone supply.'],
      ['Combination', 'A large and a small meter together, for sites with both heavy and very light use.'],
      ['Electromagnetic', 'No moving parts. Suited to large pipes and dirty water.'],
      ['Ultrasonic', 'Inline or clamped on the outside of the pipe, with very little pressure loss.'],
    ],
  },
  {
    key: 'remote', title: 'Remote reading meters', lead: 'Readings and alerts come to you, no site visit needed.',
    alt: 'Meters on three buildings send their readings through a radio mast to a dashboard, where readings and a leak alert appear',
    types: [
      ['Pulse output', 'Any mechanical meter fitted with a sensor and wired to a logger.'],
      ['Walk-by / drive-by', 'Read by radio from a handheld unit, without entering the property.'],
      ['GSM, NB-IoT, LTE-M', 'Readings sent over the mobile network, with years of battery life on NB-IoT.'],
      ['LoRaWAN', 'Long range and low power through a gateway on site. Good for estates and campuses.'],
      ['Smart ultrasonic', 'Built-in radio plus leak, tamper and backflow alerts.'],
    ],
  },
];

const ST = { panel: '#1b2f45', line: '#2d4560', pipe: '#24384f', water: '#5ec4e6', paper: '#f5f2ec', muted: '#9fb0c0', dim: '#5d7186', green: '#3fb27f', red: '#d9534f', deep: '#0a1520' };
const SCENE = 6; // seconds per scene loop
const AT = [10, 15, 25, 30, 40, 45, 55, 60, 70];
const meterKeyframes = AT.map((p) => `
  @keyframes mtAt${p} { 0%,${p}% { opacity: 0 } ${p + 2}%,94% { opacity: 1 } 100% { opacity: 0 } }
  @keyframes mtKey${p} { 0%,${p - 1}% { fill: #24384f } ${p}% { fill: #5ec4e6 } ${p + 3}%,100% { fill: #24384f } }`).join('');
const at = (p) => ({ animation: `mtAt${p} ${SCENE}s linear infinite` });
const spin = (s, reverse) => ({ transformBox: 'fill-box', transformOrigin: 'center', animation: `mtSpin ${s}s linear infinite${reverse ? ' reverse' : ''}` });
const flow = (s) => ({ animation: `mtFlow ${s}s linear infinite` });
const mono = { fontFamily: MONO };
const label = (x, y, title, sub) => (
  <g className="mt-lbl">
    <text x={x} y={y} textAnchor="middle" fill={ST.paper} fontSize="14" fontWeight="600" fontFamily={TEXT}>{title}</text>
    {sub && <text x={x} y={y + 18} textAnchor="middle" fill={ST.muted} fontSize="11.5" fontFamily={TEXT}>{sub}</text>}
  </g>
);

function PrepaidScene() {
  const keys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '✕', '0', '↵'];
  const flash = { 3: 10, 7: 25, 0: 40, 5: 55 };
  return (
    <g>
      <line x1="213" y1="70" x2="213" y2="330" stroke={ST.line} />
      <line x1="427" y1="70" x2="427" y2="330" stroke={ST.line} />

      {/* Keypad CIU: the token is typed in four groups, then accepted */}
      <rect x="52" y="64" width="110" height="200" rx="14" fill={ST.panel} stroke={ST.line} />
      <rect x="64" y="78" width="86" height="40" rx="4" fill={ST.deep} />
      {[['4821', 70, 95, 10], ['0937', 110, 95, 25], ['1166', 70, 111, 40], ['2205', 110, 111, 55]].map(([t, x, y, p]) => (
        <text key={t} x={x} y={y} fill={ST.water} fontSize="11" style={{ ...mono, ...at(p) }}>{t}</text>
      ))}
      {keys.map((k, i) => {
        const x = 66 + (i % 3) * 30, y = 130 + Math.floor(i / 3) * 31;
        return (
          <g key={k}>
            <rect x={x} y={y} width="24" height="22" rx="4" fill="#24384f" style={flash[i] ? { animation: `mtKey${flash[i]} ${SCENE}s linear infinite` } : undefined} />
            <text x={x + 12} y={y + 15} textAnchor="middle" fill={ST.muted} fontSize="10" style={mono}>{k}</text>
          </g>
        );
      })}
      <g style={at(70)}>
        <rect x="58" y="272" width="98" height="24" rx="12" fill={ST.green} fillOpacity=".18" stroke={ST.green} />
        <text x="107" y="288" textAnchor="middle" fill={ST.green} fontSize="11" fontWeight="600" style={mono}>✓ +2.0 m³</text>
      </g>
      {label(107, 322, 'Keypad (STS)', 'Type the token')}

      {/* Smart card slides into the meter, the light turns green and credit shows */}
      <g style={{ transform: 'translateY(40px)', animation: `mtCard ${SCENE}s ease-in-out infinite` }}>
        <rect x="294" y="58" width="52" height="72" rx="5" fill={ST.water} />
        <rect x="304" y="72" width="16" height="12" rx="2" fill="#d9b44a" />
        <text x="320" y="118" textAnchor="middle" fill={ST.deep} fontSize="8" fontWeight="700" style={mono}>WATER</text>
      </g>
      <rect x="270" y="130" width="100" height="134" rx="16" fill={ST.panel} stroke={ST.line} />
      <rect x="288" y="126" width="64" height="7" rx="2" fill={ST.deep} />
      <circle cx="350" cy="152" r="5" fill={ST.red} />
      <circle cx="350" cy="152" r="5" fill={ST.green} style={at(45)} />
      <rect x="285" y="172" width="70" height="28" rx="4" fill={ST.deep} />
      <text x="320" y="190" textAnchor="middle" fill={ST.water} fontSize="11" style={{ ...mono, ...at(45) }}>KES 500</text>
      <circle cx="320" cy="232" r="16" fill="none" stroke={ST.line} strokeWidth="4" />
      <path d="M210 248 H270 M370 248 H430" stroke={ST.pipe} strokeWidth="14" />
      {label(320, 322, 'Smart card', 'Insert at the meter')}

      {/* IoT: the phone pays, credit travels by radio, the meter shows it */}
      <rect x="448" y="76" width="56" height="100" rx="9" fill={ST.paper} />
      <rect x="454" y="86" width="44" height="74" rx="3" fill="#43b02a" />
      <text x="476" y="114" textAnchor="middle" fill="#fff" fontSize="9" fontWeight="700" fontFamily={TEXT}>PAY</text>
      <text x="476" y="134" textAnchor="middle" fill="#fff" fontSize="12" fontWeight="700" style={{ ...mono, ...at(10) }}>✓</text>
      <path d="M508 112 Q 566 92 584 168" fill="none" stroke={ST.water} strokeWidth="2.5" strokeDasharray="5 7" style={{ ...flow(1.2), ...at(15) }} />
      <line x1="584" y1="190" x2="584" y2="166" stroke={ST.muted} strokeWidth="2" />
      <circle cx="584" cy="164" r="4" fill={ST.water} />
      <circle cx="584" cy="164" r="8" fill="none" stroke={ST.water} style={{ transformBox: 'fill-box', transformOrigin: 'center', animation: 'mtPulse 1.6s ease-out infinite' }} />
      <rect x="530" y="190" width="80" height="96" rx="14" fill={ST.panel} stroke={ST.line} />
      <rect x="542" y="204" width="56" height="26" rx="4" fill={ST.deep} />
      <text x="570" y="221" textAnchor="middle" fill={ST.water} fontSize="10.5" style={{ ...mono, ...at(40) }}>+2.0 m³</text>
      <circle cx="570" cy="256" r="12" fill="none" stroke={ST.line} strokeWidth="4" />
      {label(533, 322, 'IoT, tokenless', 'Credit sent to the meter')}
    </g>
  );
}

function MechanicalScene() {
  return (
    <g>
      <rect x="0" y="206" width="640" height="34" fill={ST.pipe} />
      <line x1="0" y1="223" x2="640" y2="223" stroke={ST.water} strokeOpacity=".7" strokeWidth="3" strokeDasharray="18 14" style={flow(0.9)} />
      <rect x="196" y="192" width="248" height="62" rx="10" fill={ST.line} />
      <circle cx="320" cy="168" r="104" fill={ST.line} />
      <circle cx="320" cy="168" r="96" fill={ST.paper} />
      <text x="320" y="102" textAnchor="middle" fill={ST.dim} fontSize="10" letterSpacing="1.5" style={mono}>MULTI-JET · DN20</text>

      {/* Register: four fixed digits and a rolling last digit */}
      <rect x="262" y="110" width="116" height="30" rx="3" fill={ST.deep} />
      {['0', '1', '2', '4'].map((d, i) => (
        <text key={i} x={275 + i * 22} y="131" textAnchor="middle" fill={ST.paper} fontSize="17" style={mono}>{d}</text>
      ))}
      <rect x="352" y="110" width="24" height="30" fill="#7a1f1f" />
      <svg x="352" y="110" width="24" height="30" viewBox="0 0 24 30">
        <g style={{ animation: 'mtRoll 10s linear infinite' }}>
          {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 0].map((d, i) => (
            <text key={i} x="12" y={21 + i * 30} textAnchor="middle" fill={ST.paper} fontSize="17" style={mono}>{d}</text>
          ))}
        </g>
      </svg>
      <text x="320" y="156" textAnchor="middle" fill={ST.dim} fontSize="10" style={mono}>m³</text>

      {/* Sub-dials turning at different speeds, and the red leak star */}
      {[[268, 190, 1.2], [300, 204, 4], [340, 204, 12], [372, 190, 36]].map(([x, y, s]) => (
        <g key={x}>
          <circle cx={x} cy={y} r="13" fill="none" stroke={ST.muted} />
          <line x1={x} y1={y} x2={x} y2={y - 10} stroke={ST.red} strokeWidth="2" strokeLinecap="round"
            style={{ transformOrigin: `${x}px ${y}px`, animation: `mtSpin ${s}s linear infinite` }} />
        </g>
      ))}
      <polygon points="320,222 323,229 330,232 323,235 320,242 317,235 310,232 317,229" fill={ST.red} style={spin(0.6)} />

      {['Single-jet', 'Multi-jet', 'Volumetric', 'Dry-dial'].map((t, i) => (
        <g key={t} className="mt-lbl">
          <rect x={96 + i * 116} y="300" width="100" height="28" rx="14" fill="none" stroke={i === 1 ? ST.water : ST.line} />
          <text x={146 + i * 116} y="318" textAnchor="middle" fill={i === 1 ? ST.paper : ST.muted} fontSize="12" fontFamily={TEXT}>{t}</text>
        </g>
      ))}
      <text className="mt-lbl" x="320" y="352" textAnchor="middle" fill={ST.muted} fontSize="12" fontFamily={TEXT}>Water turns the dials as it passes. Read by hand, billed afterwards.</text>
    </g>
  );
}

function BulkScene() {
  return (
    <g>
      <rect x="0" y="160" width="640" height="90" fill={ST.pipe} />
      {[178, 198, 218, 238].map((y, i) => (
        <line key={y} x1="0" y1={y} x2="640" y2={y} stroke={ST.water} strokeOpacity=".55" strokeWidth="2.5" strokeDasharray="26 18" style={flow(0.5 + i * 0.08)} />
      ))}
      {[30, 222, 418, 610].map((x) => <rect key={x} x={x - 5} y="148" width="10" height="114" rx="2" fill={ST.line} />)}

      {/* Woltmann: rotor turning in the flow */}
      <rect x="66" y="132" width="118" height="146" rx="10" fill={ST.line} />
      <circle cx="125" cy="205" r="44" fill={ST.deep} />
      <g style={spin(1.1)}>
        {[0, 45, 90, 135].map((a) => <ellipse key={a} cx="125" cy="205" rx="36" ry="7" fill={ST.water} fillOpacity=".85" transform={`rotate(${a} 125 205)`} />)}
        <circle cx="125" cy="205" r="7" fill={ST.paper} />
      </g>
      {label(125, 312, 'Woltmann turbine', 'Mains and boreholes')}

      {/* Electromagnetic: coils above and below, field lines pulsing */}
      <rect x="262" y="146" width="116" height="118" rx="6" fill={ST.panel} stroke={ST.line} />
      <rect x="280" y="126" width="80" height="20" rx="4" fill="#b8792c" />
      <rect x="280" y="264" width="80" height="20" rx="4" fill="#b8792c" />
      {[290, 320, 350].map((x, i) => (
        <line key={x} x1={x} y1="150" x2={x} y2="260" stroke={ST.water} strokeWidth="2" strokeDasharray="3 6"
          style={{ animation: `mtField 1.4s ease-in-out ${i * 0.2}s infinite` }} />
      ))}
      <circle cx="268" cy="205" r="4" fill={ST.paper} /><circle cx="372" cy="205" r="4" fill={ST.paper} />
      {label(320, 312, 'Electromagnetic', 'Big pipes, dirty water')}

      {/* Ultrasonic clamp-on: sound pulses bounce across the pipe */}
      <rect x="452" y="134" width="34" height="24" rx="4" fill={ST.paper} />
      <rect x="542" y="252" width="34" height="24" rx="4" fill={ST.paper} />
      <path d="M469 160 L559 250" stroke={ST.water} strokeWidth="2.5" strokeDasharray="6 8" style={flow(0.8)} />
      <path d="M559 250 L469 160" stroke={ST.paper} strokeOpacity=".5" strokeWidth="1.5" strokeDasharray="2 10" style={flow(1.1)} />
      <path d="M469 134 V96 H520" fill="none" stroke={ST.muted} strokeWidth="1.5" />
      <rect x="520" y="70" width="98" height="46" rx="6" fill={ST.deep} stroke={ST.line} />
      <text x="569" y="89" textAnchor="middle" fill={ST.dim} fontSize="9" style={mono}>FLOW</text>
      <text x="569" y="106" textAnchor="middle" fill={ST.water} fontSize="13" style={mono}>184.6 m³/h</text>
      {label(514, 312, 'Ultrasonic', 'Clamps on, no cutting')}
    </g>
  );
}

function RemoteScene() {
  const homes = [70, 170, 270];
  const rows = [['MTR 0141', '12.4 m³', ST.green, 15], ['MTR 0142', '8.9 m³', ST.green, 30], ['MTR 0143', '15.1 m³', ST.green, 45], ['MTR 0143', 'Leak alert', ST.red, 60]];
  return (
    <g>
      {homes.map((x, i) => (
        <g key={x}>
          <path d={`M${x - 38} 330 V270 L${x} 238 L${x + 38} 270 V330`} fill={ST.panel} stroke={ST.line} />
          <path d={`M${x} 286 L380 128`} stroke={ST.water} strokeWidth="1.5" strokeDasharray="4 7" strokeOpacity=".8" style={flow(1 + i * 0.2)} />
          <circle cx={x} cy="296" r="11" fill={ST.paper} />
          <circle cx={x} cy="296" r="4" fill={ST.water} />
          <circle cx={x} cy="296" r="11" fill="none" stroke={ST.water} strokeWidth="1.5"
            style={{ transformBox: 'fill-box', transformOrigin: 'center', animation: `mtPulse 2s ease-out ${i * 0.5}s infinite` }} />
        </g>
      ))}
      <path d="M360 330 L380 128 L400 330 M366 270 H394 M371 220 H389 M375 170 H385 M366 270 L389 220 M394 270 L371 220" fill="none" stroke={ST.muted} strokeWidth="2" />
      <circle cx="380" cy="124" r="6" fill={ST.water} />
      <circle cx="380" cy="124" r="10" fill="none" stroke={ST.water} style={{ transformBox: 'fill-box', transformOrigin: 'center', animation: 'mtPulse 1.6s ease-out infinite' }} />
      <path d="M392 118 L448 96" stroke={ST.water} strokeWidth="2" strokeDasharray="4 7" style={flow(0.8)} />

      <rect x="450" y="48" width="170" height="230" rx="8" fill={ST.paper} />
      <text x="466" y="76" fill={ST.pipe} fontSize="13" fontWeight="700" fontFamily={DISPLAY}>Readings</text>
      <text x="604" y="76" textAnchor="end" fill={ST.dim} fontSize="9" style={mono}>LIVE</text>
      <line x1="466" y1="88" x2="604" y2="88" stroke="#d9d2c5" />
      {rows.map(([m, v, c, p], i) => (
        <g key={i} style={at(p)}>
          <circle cx="470" cy={112 + i * 38} r="4" fill={c} />
          <text x="482" y={116 + i * 38} fill={ST.pipe} fontSize="11" style={mono}>{m}</text>
          <text x="604" y={116 + i * 38} textAnchor="end" fill={c === ST.red ? ST.red : ST.pipe} fontSize="11" fontWeight={c === ST.red ? 700 : 400} style={mono}>{v}</text>
        </g>
      ))}
      <text className="mt-lbl" x="24" y="368" fill={ST.muted} fontSize="11.5" style={mono}>Pulse · Walk-by · GSM · NB-IoT · LoRaWAN</text>
    </g>
  );
}

const SCENES = { prepaid: PrepaidScene, mechanical: MechanicalScene, bulk: BulkScene, remote: RemoteScene };
// Phone layout: each scene is cropped to its drawing and the labels move below it as normal text.
const NARROW_VIEW = { prepaid: '0 54 640 252', mechanical: '96 58 448 222', bulk: '0 62 640 226', remote: '0 40 640 300' };
const NARROW_CAPS = {
  prepaid: [['Keypad (STS)', 'Type the token'], ['Smart card', 'Insert at the meter'], ['IoT, tokenless', 'Credit sent to the meter']],
  mechanical: [['Dials turn as water passes', 'Read by hand, billed afterwards']],
  bulk: [['Woltmann', 'Mains, boreholes'], ['Electromagnetic', 'Big pipes, dirty water'], ['Ultrasonic', 'Clamps on the pipe']],
  remote: [['Meters report by radio', 'Readings and leak alerts reach your dashboard']],
};

function MeterShowcase() {
  const [active, setActive] = useState(0);
  const [auto, setAuto] = useState(true);
  const [paused, setPaused] = useState(false);
  const m = METERS[active];
  const Scene = SCENES[m.key];
  const narrow = useNarrow();
  const caps = NARROW_CAPS[m.key];
  const pick = (i) => { setActive(i); setAuto(false); };
  // Pause only for a real mouse hover; on touch screens a tap would otherwise pause it for good.
  const hover = (on) => (e) => { if (e.pointerType === 'mouse') setPaused(on); };
  return (
    <div className="mw-meters" onPointerEnter={hover(true)} onPointerLeave={hover(false)}>
      <style>{`
        .mw-meters { display: grid; grid-template-columns: 1.25fr 1fr; gap: 40px; align-items: start }
        @media (max-width: 880px) { .mw-meters { grid-template-columns: 1fr; gap: 20px } }
        .mt-caps { display: none }
        @media (max-width: 600px) {
          .mt-lbl { display: none }
          .mt-caps { display: grid; gap: 8px; margin-top: 10px }
          .mt-tab { padding: 14px 0 12px }
          .mt-title { font-size: 18px !important }
          .mt-body { padding-left: 0 !important }
        }
        @keyframes mtSpin { to { transform: rotate(360deg) } }
        @keyframes mtFlow { to { stroke-dashoffset: -64 } }
        @keyframes mtRoll { to { transform: translateY(-300px) } }
        @keyframes mtPulse { 0% { transform: scale(1); opacity: .9 } 100% { transform: scale(3.2); opacity: 0 } }
        @keyframes mtField { 0%,100% { opacity: .15 } 50% { opacity: 1 } }
        @keyframes mtCard { 0%,6% { transform: translateY(-14px); opacity: 0 } 12% { transform: translateY(-14px); opacity: 1 } 38%,92% { transform: translateY(40px); opacity: 1 } 100% { transform: translateY(40px); opacity: 0 } }
        @keyframes mtFade { from { opacity: 0 } to { opacity: 1 } }
        @keyframes mtBar { from { transform: scaleX(0) } to { transform: scaleX(1) } }
        ${meterKeyframes}
        .mt-stage { animation: mtFade .45s ease-out }
        .mt-tab { width: 100%; display: grid; grid-template-columns: 34px 1fr auto; align-items: baseline; gap: 8px; background: none; border: none; padding: 16px 0 14px; cursor: pointer; text-align: left }
        .mt-tab:hover .mt-title { color: ${C.water} }
        @media (prefers-reduced-motion: reduce) { .mw-meters *, .mw-meters svg * { animation: none !important } }
      `}</style>

      <div>
      <div role="img" aria-label={m.alt} style={{ position: 'relative', aspectRatio: narrow ? '16 / 8' : '16 / 10', borderRadius: 6, overflow: 'hidden', background: C.night }}>
        <svg key={m.key} className="mt-stage" viewBox={narrow ? NARROW_VIEW[m.key] : '0 0 640 400'} width="100%" height="100%" style={{ position: 'absolute', inset: 0 }}>
          <defs>
            <pattern id="mtGrid" width="32" height="32" patternUnits="userSpaceOnUse">
              <path d="M32 0H0V32" fill="none" stroke="#ffffff" strokeOpacity=".04" />
            </pattern>
          </defs>
          <rect width="640" height="400" fill="url(#mtGrid)" />
          <text className="mt-lbl" x="24" y="34" fill={ST.muted} fontSize="11" letterSpacing="1.5" style={mono}>{String(active + 1).padStart(2, '0')} / 04 · {m.title.toUpperCase()}</text>
          <Scene />
        </svg>
      </div>
      <div className="mt-caps" aria-hidden="true" style={{ gridTemplateColumns: `repeat(${caps.length}, 1fr)` }}>
        {caps.map(([t, d]) => (
          <div key={t} style={{ textAlign: caps.length > 1 ? 'center' : 'left' }}>
            <div style={{ fontFamily: TEXT, fontSize: 13, fontWeight: 600, color: C.ink }}>{t}</div>
            <div style={{ fontFamily: TEXT, fontSize: 12, color: C.muted, lineHeight: 1.35 }}>{d}</div>
          </div>
        ))}
      </div>
      </div>

      <div>
        {METERS.map((x, i) => {
          const on = i === active;
          return (
            <div key={x.key} style={{ borderTop: `1px solid ${on ? C.ink : C.rule}`, position: 'relative' }}>
              {on && auto && (
                <div onAnimationEnd={(e) => { if (e.target === e.currentTarget) setActive((a) => (a + 1) % METERS.length); }}
                  style={{ position: 'absolute', top: -1, left: 0, right: 0, height: 2, background: C.water, transformOrigin: 'left', animation: 'mtBar 9s linear forwards', animationPlayState: paused ? 'paused' : 'running' }} />
              )}
              <button className="mt-tab" onClick={() => pick(i)} aria-expanded={on} aria-controls={`mt-${x.key}`}>
                <span style={{ fontFamily: MONO, fontSize: 13, color: on ? C.water : C.muted }}>{String(i + 1).padStart(2, '0')}</span>
                <span className="mt-title" style={{ fontFamily: DISPLAY, fontSize: 21, fontWeight: 700, color: on ? C.ink : C.body, transition: 'color .15s' }}>{x.title}</span>
                <span aria-hidden="true" style={{ fontFamily: MONO, fontSize: 16, color: C.muted }}>{on ? '–' : '+'}</span>
              </button>
              {on && (
                <div id={`mt-${x.key}`} className="mt-body" style={{ padding: '0 0 18px 42px' }}>
                  <p style={{ fontFamily: TEXT, fontSize: 15, color: C.body, margin: '0 0 10px' }}>{x.lead}</p>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {x.types.map(([t, d]) => (
                      <li key={t} style={{ fontFamily: TEXT, fontSize: 14.5, lineHeight: 1.5, color: C.body, padding: '6px 0', borderTop: `1px dashed ${C.rule}` }}>
                        <strong style={{ color: C.ink, fontWeight: 600 }}>{t}.</strong> {d}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          );
        })}
        <div style={{ borderTop: `1px solid ${C.rule}` }} />
      </div>
    </div>
  );
}

// ───────────── Page ─────────────
export default function HomePage() {
  const navigate = useNavigate();
  const [site, setSite] = useState({ contact: {}, mediaUrl: null });
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    try { localStorage.setItem('mw_seen_home', '1'); } catch { /* private mode */ }
    axios.get('/api/public/site').then((r) => setSite(r.data)).catch(() => {});
  }, []);

  const { phone, email, whatsapp } = site.contact || {};
  const waNumber = whatsapp ? whatsapp.replace(/\D/g, '').replace(/^0/, '254') : null;
  const scrollTo = (id) => { setMenuOpen(false); document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }); };
  const SECTIONS = [['services', 'Services'], ['meters', 'Meters'], ['why', 'Why us'], ['how', 'Buying water'], ['contact', 'Contact']];

  const primary = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minHeight: 58, padding: '0 30px', borderRadius: 4,
    fontSize: 17, fontWeight: 600, fontFamily: TEXT, cursor: 'pointer', border: 'none', background: '#5ec4e6', color: C.ink,
  };
  const secondary = { ...primary, background: 'transparent', color: '#f5f2ec', border: '1px solid rgba(245,242,236,0.45)' };
  const h2 = { fontFamily: DISPLAY, color: C.ink, fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 700, lineHeight: 1.1, margin: '0 0 14px', letterSpacing: -0.5 };
  const p = { fontFamily: TEXT, color: C.body, fontSize: 17, lineHeight: 1.65, margin: 0 };
  const link = { display: 'inline-block', fontFamily: TEXT, fontSize: 16, fontWeight: 600, color: C.ink, textDecoration: 'none', borderBottom: `2px solid ${C.water}`, paddingBottom: 2, marginRight: 24, marginBottom: 12 };

  return (
    <div style={{ background: C.paper, minHeight: '100vh', fontFamily: TEXT, color: C.ink, overflowX: 'hidden' }}>
      <style>{`
        .mw-hero { display: grid; grid-template-columns: 1.05fr 1fr; gap: 56px; align-items: center }
        .mw-two { display: grid; grid-template-columns: 1fr 1fr; gap: 0 56px }
        .mw-row:hover h3 { color: ${C.water} }
        .mw-btn { transition: transform .15s ease, background .15s ease }
        .mw-btn:hover { transform: translateY(-1px) }
        .mw-menu-btn { display: none }
        @media (max-width: 880px) { .mw-hero, .mw-two { grid-template-columns: 1fr } .mw-nav { display: none !important } .mw-row { grid-template-columns: 1fr !important } .mw-menu-btn { display: inline-flex } }
        @media (max-width: 600px) {
          .mw-hero { gap: 40px }
          .mw-cta { max-width: none !important; flex-basis: 100% !important; width: 100% }
          .mw-signin-top { display: none }
          .mw-home section { scroll-margin-top: 8px }
        }
        .mw-home a:focus-visible, .mw-home button:focus-visible, .mw-home input:focus-visible, .mw-home select:focus-visible, .mw-home textarea:focus-visible { outline: 3px solid ${C.water}; outline-offset: 2px }
      `}</style>
      <div className="mw-home">
        {/* ── Top bar ── */}
        <header style={{ background: '#ffffff', borderBottom: '1px solid #e3ddd2' }}>
          <div style={{ ...wrap, display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 76 }}>
            <Logo height={38} />
            <nav className="mw-nav" aria-label="Page sections" style={{ display: 'flex', gap: 28 }}>
              {SECTIONS.map(([id, t]) => (
                <button key={id} onClick={() => scrollTo(id)} style={{ background: 'none', border: 'none', color: '#334155', fontSize: 16, fontWeight: 500, fontFamily: TEXT, cursor: 'pointer', padding: 0 }}>{t}</button>
              ))}
            </nav>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="mw-signin-top" onClick={() => navigate('/login')} style={{ background: '#1a5fa4', border: '1px solid #1a5fa4', color: '#ffffff', borderRadius: 4, padding: '8px 16px', fontSize: 14, fontWeight: 600, fontFamily: TEXT, cursor: 'pointer' }}>Sign in</button>
              <button className="mw-menu-btn" onClick={() => setMenuOpen((o) => !o)} aria-expanded={menuOpen} aria-controls="mw-mobile-menu"
                style={{ alignItems: 'center', gap: 8, background: 'none', border: '1px solid #cbd5e1', color: '#0f172a', borderRadius: 4, padding: '8px 14px', minHeight: 40, fontSize: 14, fontWeight: 600, fontFamily: TEXT, cursor: 'pointer' }}>
                <svg width="16" height="12" viewBox="0 0 16 12" aria-hidden="true">
                  {menuOpen
                    ? <path d="M3 1l10 10M13 1L3 11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    : <path d="M1 1h14M1 6h14M1 11h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />}
                </svg>
                Menu
              </button>
            </div>
          </div>
          {menuOpen && (
            <nav id="mw-mobile-menu" aria-label="Page sections" style={{ ...wrap, paddingBottom: 18 }}>
              {SECTIONS.map(([id, t]) => (
                <button key={id} onClick={() => scrollTo(id)}
                  style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', borderTop: '1px solid #e2e8f0', color: '#0f172a', fontSize: 17, fontFamily: TEXT, padding: '14px 0', cursor: 'pointer' }}>{t}</button>
              ))}
              <div style={{ display: 'flex', gap: 10, paddingTop: 14, borderTop: '1px solid #e2e8f0' }}>
                <button onClick={() => navigate('/pay?buy=1')} style={{ flex: 1, minHeight: 48, borderRadius: 4, border: 'none', background: '#1a5fa4', color: '#ffffff', fontSize: 15, fontWeight: 600, fontFamily: TEXT, cursor: 'pointer' }}>Buy a token</button>
                <button onClick={() => navigate('/login')} style={{ flex: 1, minHeight: 48, borderRadius: 4, border: '1px solid #cbd5e1', background: 'none', color: '#0f172a', fontSize: 15, fontWeight: 600, fontFamily: TEXT, cursor: 'pointer' }}>Sign in</button>
              </div>
            </nav>
          )}
        </header>

        {/* ── Hero ── */}
        <section style={{ background: C.night, padding: '64px 0 84px', borderBottom: '6px solid #5ec4e6' }}>
          <div style={wrap} className="mw-hero">
            <div>
              <SectionLabel n="Kenya" light>Prepaid water</SectionLabel>
              <h1 style={{ fontFamily: DISPLAY, color: '#f5f2ec', fontSize: 'clamp(38px, 6vw, 68px)', lineHeight: 1.02, fontWeight: 800, margin: '0 0 22px', letterSpacing: -1.5 }}>
                Water on when they pay. Money straight to you.
              </h1>
              <p style={{ ...p, color: '#c6d2dd', fontSize: 18, maxWidth: 520, marginBottom: 34 }}>
                Smart prepaid meters for landlords, estates and water providers. Tenants pay from their phone, the token arrives in seconds,
                and the money lands in your own paybill or bank account.
              </p>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <button className="mw-btn mw-cta" onClick={() => navigate('/pay?buy=1')} style={{ ...primary, flex: '1 1 220px', maxWidth: 280 }}>Buy a water token</button>
                <button className="mw-btn mw-cta" onClick={() => navigate('/login')} style={{ ...secondary, flex: '1 1 220px', maxWidth: 280 }}>Sign in to manage</button>
              </div>
              <button onClick={() => navigate('/pay?retrieve=1')} style={{ background: 'none', border: 'none', color: '#9fb0c0', fontSize: 14, fontFamily: TEXT, cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3, marginTop: 18, padding: 0 }}>
                Already paid? Get your token again
              </button>
            </div>
            {/* Animation slot */}
            <MediaSlot url={site.mediaUrl} />
          </div>
        </section>

        {/* ── Why us ── */}
        <section id="why" style={{ padding: '80px 0 56px' }}>
          <div style={wrap}>
            <SectionLabel n="01">Why us</SectionLabel>
            <h2 style={{ ...h2, maxWidth: 720 }}>Built around one idea: the money is yours.</h2>
            <div className="mw-two" style={{ marginTop: 34 }}>
              {WHY.map(([lead, text], i) => (
                <div key={lead} style={{ display: 'grid', gridTemplateColumns: '42px 1fr', gap: 8, padding: '18px 0', borderTop: `1px solid ${C.rule}` }}>
                  <span style={{ fontFamily: MONO, fontSize: 13, color: C.muted, paddingTop: 3 }}>{String(i + 1).padStart(2, '0')}</span>
                  <p style={{ ...p, fontSize: 16 }}><strong style={{ color: C.ink, fontWeight: 600 }}>{lead}</strong> {text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Services ── */}
        <section id="services" style={{ padding: '64px 0', background: C.paper2 }}>
          <div style={wrap}>
            <SectionLabel n="02">What we do</SectionLabel>
            <h2 style={h2}>Everything between the meter and the bank.</h2>
            <div style={{ marginTop: 30 }}>
              {SERVICES.map(([title, text]) => (
                <div key={title} className="mw-row" style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 1fr) 2fr', gap: '6px 40px', padding: '22px 0', borderTop: `1px solid ${C.rule}` }}>
                  <h3 style={{ fontFamily: DISPLAY, fontSize: 21, fontWeight: 700, margin: 0, color: C.ink, transition: 'color .15s' }}>{title}</h3>
                  <p style={{ ...p, fontSize: 16 }}>{text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Meter types ── */}
        <section id="meters" style={{ padding: '72px 0 64px' }}>
          <div style={wrap}>
            <SectionLabel n="03">Meters</SectionLabel>
            <h2 style={{ ...h2, maxWidth: 760 }}>From the tap to the trunk main.</h2>
            <p style={{ ...p, maxWidth: 680, marginBottom: 34 }}>
              Prepaid meters for homes and kiosks, mechanical and bulk meters on the supply, and remote reading to tie them together.
              Not sure which you need? <button onClick={() => scrollTo('contact')} style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', color: C.water, textDecoration: 'underline', textUnderlineOffset: 3, cursor: 'pointer' }}>Ask us</button>.
            </p>
            <MeterShowcase />
          </div>
        </section>

        {/* ── Buying water ── */}
        <section id="how" style={{ padding: '72px 0' }}>
          <div style={wrap}>
            <SectionLabel n="04">For customers</SectionLabel>
            <h2 style={h2}>Buying water takes under a minute.</h2>
            <p style={{ ...p, marginBottom: 34 }}>No queues, no cash and no waiting for an office to open.</p>
            <ol style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 28 }}>
              {STEPS.map(([title, text], i) => (
                <li key={title} style={{ borderTop: `2px solid ${C.ink}`, paddingTop: 18 }}>
                  <div style={{ fontFamily: DISPLAY, fontSize: 56, fontWeight: 800, color: C.water, lineHeight: 1, marginBottom: 10 }}>{i + 1}</div>
                  <h3 style={{ fontFamily: DISPLAY, fontSize: 22, fontWeight: 700, margin: '0 0 8px' }}>{title}</h3>
                  <p style={{ ...p, fontSize: 16 }}>{text}</p>
                </li>
              ))}
            </ol>
            <button className="mw-btn mw-cta" onClick={() => navigate('/pay?buy=1')} style={{ ...primary, background: C.ink, color: '#f5f2ec', marginTop: 38, minWidth: 260, maxWidth: 'none' }}>Buy a water token now</button>
          </div>
        </section>

        {/* ── Who it's for ── */}
        <section style={{ padding: '0 0 24px' }}>
          <div style={{ ...wrap }}>
            <p style={{ fontFamily: DISPLAY, fontSize: 'clamp(18px, 2.4vw, 24px)', lineHeight: 1.5, color: C.body, margin: 0, borderTop: `1px solid ${C.rule}`, paddingTop: 28 }}>
              Used by {AUDIENCE.map((a, i) => (
                <span key={a}><span style={{ color: C.ink, fontWeight: 700 }}>{a.toLowerCase()}</span>{i < AUDIENCE.length - 2 ? ', ' : i === AUDIENCE.length - 2 ? ' and ' : '.'}</span>
              ))}
            </p>
          </div>
        </section>

        {/* ── Contact ── */}
        <section id="contact" style={{ padding: '64px 0 84px' }}>
          <div style={wrap} className="mw-two">
            <div style={{ marginBottom: 28 }}>
              <SectionLabel n="05">Contact</SectionLabel>
              <h2 style={h2}>Tell us about your property.</h2>
              <p style={{ ...p, marginBottom: 26 }}>
                We’ll show you how it would work for you, including collecting into your own paybill or bank account.
              </p>
              <div>
                {waNumber && <a href={`https://wa.me/${waNumber}?text=${encodeURIComponent('Hello Smart Maji, I’d like to know more about prepaid water meters.')}`} target="_blank" rel="noreferrer" style={link}>WhatsApp us</a>}
                {phone && <a href={`tel:${phone.replace(/[^\d+]/g, '')}`} style={link}>Call {phone}</a>}
                {email && <a href={`mailto:${email}?subject=${encodeURIComponent('Enquiry about prepaid water meters')}`} style={link}>{email}</a>}
              </div>
              {!waNumber && !phone && !email && <p style={{ ...p, fontSize: 14, color: C.muted }}>Use the form and we’ll get straight back to you.</p>}
            </div>
            <EnquiryForm />
          </div>
        </section>

        {/* ── Footer ── */}
        <footer style={{ background: C.night, color: '#9fb0c0', padding: '30px 0' }}>
          <div style={{ ...wrap, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 14, fontSize: 14 }}>
            <span>© {new Date().getFullYear()} Smart Maji · Majic Water Systems · Nairobi, Kenya</span>
            <span style={{ display: 'flex', gap: 22, flexWrap: 'wrap' }}>
              {[['/pay?buy=1', 'Buy token'], ['/pay?retrieve=1', 'Retrieve token'], ['/login', 'Sign in']].map(([to, t]) => (
                <button key={to} onClick={() => navigate(to)} style={{ background: 'none', border: 'none', color: '#c6d2dd', cursor: 'pointer', padding: 0, fontFamily: TEXT, fontSize: 14 }}>{t}</button>
              ))}
            </span>
          </div>
        </footer>
      </div>
    </div>
  );
}
